import React from 'react';
import { Spin, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faGripVertical, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import { getMediaURL } from '../../../../context/API';
import {
  ActionIconButton,
  OptionCardDraggable,
  SoldOutButton,
  OptionButtonsContainer,
} from '../Styles';
import { DraggableOptionProps } from '../types';
import { OptionThumbnail } from '../OptionThumbnail';

export const DraggableOption = React.memo<DraggableOptionProps>(
  ({
    option,
    handleEditOption,
    handleOptionRemove,
    soldOutLoading,
  }) => {
    const { isDarkMode } = useTheme();
    const colorMode = isDarkMode ? 'dark' : 'light';
    const imageUrl = option.image || option.picture;

    if (!option || !option.id) return null;

    const handleDownload = async () => {
      if (!imageUrl) return;
      const resolvedUrl = imageUrl.startsWith('http') ? imageUrl : (getMediaURL(imageUrl) ?? imageUrl);
      try {
        const response = await fetch(resolvedUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        const ext = resolvedUrl.split('.').pop()?.split('?')[0] || 'jpg';
        link.download = `${option.option.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${ext}`;
        link.click();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        console.error('Image download failed:', err);
      }
    };

    return (
      <>
      <OptionCardDraggable
        $isDark={colorMode === 'dark'}
        $isDraggable={true}
        style={{ cursor: 'grab', padding: '10px 12px', alignItems: 'flex-start' }}
      >
        {/* Left: grip + image + text */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <FontAwesomeIcon
            icon={faGripVertical}
            fontSize={15}
            color={colorMode === 'dark' ? '#9ca3af' : '#b0b8c4'}
            style={{ cursor: 'grab', flexShrink: 0 }}
          />

          {/* Image */}
          <OptionThumbnail imageUrl={imageUrl} altText={option.option} isDark={colorMode === 'dark'} />

          {/* Text */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Typography.Text
              strong
              style={{
                fontSize: 14,
                color: colorMode === 'dark' ? '#fff' : '#1a1a1a',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.option}
            </Typography.Text>
            {(option as any).description && (
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 12,
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {(option as any).description}
              </Typography.Text>
            )}
            <Typography.Text
              style={{
                fontSize: 13,
                color: colorMode === 'dark' ? '#9ca3af' : '#718096',
                lineHeight: '18px',
                fontWeight: 500,
              }}
            >
              {option.price !== undefined && !isNaN(option.price)
                ? `$${(option.price || 0).toFixed(2)}`
                : '$0.00'}
            </Typography.Text>
          </div>
        </div>

        {/* Right: actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0, marginLeft: 8 }}>
          <SoldOutButton
            size="small"
            onClick={() => handleEditOption(option, true)}
            loading={soldOutLoading === option.id}
            icon={soldOutLoading === option.id ? <Spin size="small" /> : null}
          >
            {option.sold_out ? 'Set In Stock' : 'Set Sold Out'}
          </SoldOutButton>
          <OptionButtonsContainer>
            {imageUrl && (
              <ActionIconButton
                iconColor="#52c41a"
                onClick={handleDownload}
                title="Download image"
              >
                <FontAwesomeIcon icon={faDownload} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
              </ActionIconButton>
            )}
            <ActionIconButton
              iconColor="#2D6CCA"
              onClick={() => option && handleEditOption(option)}
            >
              <FontAwesomeIcon icon={faPen} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
            </ActionIconButton>
            <ActionIconButton
              iconColor="#f5222d"
              onClick={() => option?.id && handleOptionRemove(option.id)}
            >
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={colorMode === 'dark' ? '#fff' : '#222222'} />
            </ActionIconButton>
          </OptionButtonsContainer>
        </div>
      </OptionCardDraggable>

</>
    );
  }
);

DraggableOption.displayName = 'DraggableOption';
